import React, { useEffect } from "react";
import { List, ListItem, ListItemText, Checkbox, Select, MenuItem, Tooltip, CircularProgress } from "@mui/material";
import MDTypography from "components/MDTypography";
import { styled } from "@mui/system";
import MDBox from "components/MDBox";
import colors from "assets/theme-dark/base/colors";
import pxToRem from "assets/theme/functions/pxToRem";

interface MergerTableColumnsProps {
  columns: string[];
  selectedDatasetId: string;
  selectedColumns: string[];
  joinColumn: string | null;
  handleColumnSelect: (datasetId: string, columns: string[], joinColumn: string | null) => void;
  tableOrder: string[];
  isLoading: boolean;
  isReplacingView: boolean;
  joinWithColumn: string[];
}

const MergerTableColumns: React.FC<MergerTableColumnsProps> = ({
  columns,
  selectedDatasetId,
  selectedColumns,
  joinColumn,
  handleColumnSelect,
  tableOrder,
  isLoading,
  isReplacingView,
  joinWithColumn,
}) => {
  const isFirstTable = tableOrder.length > 0 && tableOrder[0] === selectedDatasetId;
  const FILTERED_COLUMNS = [
    "DATASETID",
    "SOURCEFILEID",
    "CUBEID",
    "datasetId",
    "cubeId",
    "CRUXY_ID",
    "WASALREADYMERGEDFROMDATASETS",
  ];

  useEffect(() => {
    if (isFirstTable && columns.includes("FX_UNIQUE_ID") && !selectedColumns.includes("FX_UNIQUE_ID")) {
      handleColumnSelect(selectedDatasetId, [...selectedColumns, "FX_UNIQUE_ID"], null);
    }
  }, [columns, selectedColumns, selectedDatasetId, handleColumnSelect, isFirstTable]);

  const handleCheckboxChange = (column: string) => {
    if (column === "FX_UNIQUE_ID" && isFirstTable) {
      return;
    }
    if (isReplacingView && joinWithColumn.includes(column)) {
      return;
    }
    const updatedColumns = selectedColumns.includes(column)
      ? selectedColumns.filter((col) => col !== column)
      : [...selectedColumns, column];
    handleColumnSelect(selectedDatasetId, updatedColumns, joinColumn);
  };

  const truncateColumnName = (name: string): string => (name.length > 10 ? `${name.slice(0, 10)}...` : name);

  const displayedColumns = isFirstTable ? columns : columns.filter((column) => column !== "FX_UNIQUE_ID");

  const joinColumnOptions = columns.filter(
    (column) => column !== "FX_UNIQUE_ID" && column !== "CRUXY_ID" && !column.includes("SCORE_")
  );

  return (
    <StyledCard>
      {!isFirstTable && (
        <>
          <MDTypography variant="subtitle2" sx={{ fontSize: "0.8rem", mb: 0.5, textAlign: "start" }}>
            Join Column:
          </MDTypography>
          <StyledSelect
            value={joinColumn || ""}
            onChange={(e) => handleColumnSelect(selectedDatasetId, selectedColumns, (e.target.value as string) || null)}
            fullWidth
            size="small"
          >
            <MenuItem value="">None</MenuItem>
            {joinColumnOptions.map((column) => (
              <MenuItem key={column} value={column}>
                <Tooltip title={column} arrow>
                  <span>{column.length > 12 ? `${column.slice(0, 10)}...` : column}</span>
                </Tooltip>
              </MenuItem>
            ))}
          </StyledSelect>
        </>
      )}
      <MDTypography variant="h6" fontWeight="medium" sx={{ color: colors.text.main, fontSize: "1.1rem", mb: 1 }}>
        Table Columns
      </MDTypography>
      <hr style={{ backgroundColor: colors.grey[900], marginBottom: "0.5rem" }} />
      {displayedColumns.length === 0 ? (
        isLoading ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" height="100px">
            <CircularProgress size={24} />
          </MDBox>
        ) : (
          <MDTypography variant="body1" color="textSecondary" sx={{ textAlign: "center" }}>
            No data.
          </MDTypography>
        )
      ) : (
        <ScrollBox>
          <List dense>
            {displayedColumns
              .filter((column) => !FILTERED_COLUMNS.includes(column.toUpperCase()) && !column.includes("SCORE_"))
              .map((column) => (
                <ListItem
                  key={column}
                  sx={{
                    padding: "0.3rem 0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                  }}
                >
                  <Tooltip
                    title={
                      isReplacingView && joinWithColumn.includes(column)
                        ? "This column is used in a join and cannot be deselected"
                        : ""
                    }
                    arrow
                  >
                    <span>
                      <Checkbox
                        size="small"
                        checked={selectedColumns.includes(column)}
                        onChange={() => handleCheckboxChange(column)}
                        disabled={
                          (column === "FX_UNIQUE_ID" && isFirstTable) ||
                          (isReplacingView && joinWithColumn.includes(column))
                        }
                        sx={{
                          color: colors.primary.main,
                          "&.Mui-checked": {
                            color: colors.secondary.main,
                            border: "none",
                          },
                          "&:hover": {
                            color: colors.primary.dark,
                            transform: "scale(1.04)",
                          },
                          border: `1px solid ${colors.primary.main}`,
                          borderRadius: "4px",
                          padding: "0",
                          marginRight: "0.2rem",
                          transition: "all 0.2s ease-in-out",
                        }}
                      />
                    </span>
                  </Tooltip>
                  <Tooltip title={column} arrow>
                    <ListItemText
                      primary={truncateColumnName(column)}
                      primaryTypographyProps={{
                        fontSize: "0.75em",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    />
                  </Tooltip>
                </ListItem>
              ))}
          </List>
        </ScrollBox>
      )}
    </StyledCard>
  );
};

const ScrollBox = styled(MDBox)`
  max-height: 300px;
  overflow-y: auto;
  border-radius: 8px;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.palette.primary.main};
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${(props) => props.theme.palette.primary.dark};
  }
`;

const StyledCard = styled(MDBox)`
  width: ${pxToRem(150)};
  min-width: ${pxToRem(150)};
  box-sizing: border-box;
  flex-shrink: 0; /* Prevent flex shrinkage */
  background: ${colors.background.default};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid ${colors.grey[300]};
`;
const StyledSelect = styled(Select)`
  background: ${colors.secondary.main};
  font-size: 0.7rem;
  border-radius: 4px;
  transition: all 0.2s ease-in-out;
  margin-bottom: 0.5rem;

  &:hover {
    background: ${colors.secondary.focus};
    transform: scale(1.02);
  }

  & .MuiSelect-select {
    padding: 8px 14px;
  }

  & .MuiOutlinedInput-notchedOutline {
    border-color: ${colors.grey[300]};
  }
`;

export default MergerTableColumns;
